import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const AUTH_FUNCTION_URL = 'https://kowvjmkfoxeeidwiphvy.supabase.co/functions/v1/auth';
const TOKEN_STORAGE_KEY = 'site_auth_token';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtvd3ZqbWtmb3hlZWlkd2lwaHZ5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEyMDQ0ODYsImV4cCI6MjA4Njc4MDQ4Nn0.JODio5rBinjzOIbK5wXYcfT0en8gPZOojrqKjbv2ANY';
const AUTH_HEADERS = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Apikey': SUPABASE_ANON_KEY,
};

export default function PasswordGate({ children }: { children: React.ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(AUTH_FUNCTION_URL, {
      method: 'POST',
      headers: AUTH_HEADERS,
      body: JSON.stringify({ action: 'check', token }),
    })
      .then(r => r.json())
      .then(data => {
        setAuthenticated(!!data.authenticated);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(AUTH_FUNCTION_URL, {
        method: 'POST',
        headers: AUTH_HEADERS,
        body: JSON.stringify({ action: 'login', password }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
        setAuthenticated(true);
        setError(false);
      } else {
        setError(true);
        setShake(true);
        setPassword('');
        setTimeout(() => setShake(false), 500);
      }
    } catch {
      setError(true);
      setShake(true);
      setPassword('');
      setTimeout(() => setShake(false), 500);
    }
  };

  if (loading) return null;
  if (authenticated) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black">
      <style>{`
        @keyframes gate-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
        .gate-shake { animation: gate-shake 0.4s ease-in-out; }
      `}</style>

      <div className="text-center mb-4">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold tracking-wide text-white mb-3">
          Saumya Patni
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={shake ? 'gate-shake' : ''}>
        <div
          className="pill-border"
          style={{
            width: focused ? 'min(380px, calc(100vw - 3rem))' : '300px',
            transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: focused ? 1 : 0.7,
          }}
        >
          <div className="flex items-center rounded-full px-4 py-[11px] gap-3 bg-black">
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="Password"
              autoFocus
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-white placeholder-white/40"
            />
            <button
              type="submit"
              onMouseDown={e => e.preventDefault()}
              className="w-7 h-7 rounded-full bg-white text-black flex items-center justify-center flex-shrink-0 hover:bg-white/90 transition-all duration-200"
            >
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </form>

      {error && (
        <p className="text-red-400/80 text-xs mt-4 tracking-wide">
          Wrong password
        </p>
      )}

      <p className="text-white/25 text-xs mt-3 tracking-wide">
        Don't have the password?{' '}
        <a href="mailto:saum.patni@gmail.com" className="text-white/40 underline underline-offset-2 hover:text-white/60 transition-colors">
          Get in touch
        </a>
      </p>
    </div>
  );
}
